/** The space, as the thing it draws. Pick a canvas, click what you see, edit it in the tray. */

import type { CSSProperties } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { EmptyState, StatusBadge } from "@/components/ui";

import { canvasLayout, placeable } from "../../canvas/composition";
import { SpaceVerbs } from "../components/SpaceVerbs";
import { Muted } from "../components/Text";
import { answersTo } from "../lib/canvasAddress";
import { canvasesOf, isBundle } from "../lib/catalog";
import { isLive, runStatusOf } from "../lib/runState";
import { addedActivityBySlug, addedActivitySlugs } from "../spaces/lib/address";
import { useAddActivity, useSpaceActivities } from "../spaces/lib/queries";
import type { AddedActivityRow, NamedCanvas, SpaceRow } from "../types";

import { ArrangeLayer } from "./ArrangeLayer";
import { NodeOutline } from "./NodeOutline";
import { PlanStage, SpaceCanvas, type StageDrop, type StageSelection } from "./SpaceCanvas";
import { MoveIcon, PointerIcon } from "./LayerIcons";
import type { Locks } from "./LayersPanel";
import { PanelColumn } from "./tray/PanelColumn";
import { TrayPanel } from "./tray/TrayPanel";
import {
  isPanelId,
  isWidePanel,
  offeredPanels,
  panelForKey,
  shownPanel,
  type PanelId,
  type Subject,
} from "./tray/panels";
import {
  drawOrder,
  placementsByRow,
  reordered,
  useArrange,
  useArrangement,
  type Placement,
} from "./lib/arrangement";
import { droppedAt, type StagePoint } from "./lib/drop";
import { declaredKinds } from "./lib/kinds";
import { readLocks, writeLocks } from "./lib/locks";
import { trayBodyWidth } from "./lib/tray";

/** What a click on the stage does: reach the activity, or move its layer. */
type Tool = "interact" | "move";

const TOOLS: readonly { id: Tool; label: string; key: string }[] = [
  { id: "interact", label: "Interact", key: "v" },
  { id: "move", label: "Move", key: "m" },
];

/** The addressable part of this view: the canvas drawn, the panel open, the subject selected. */
export interface EditorAddress {
  readonly canvas?: string | undefined;
  readonly panel?: string | undefined;
  /** The added activity's slug, never its id. */
  readonly select?: string | undefined;
}

/** The added activities whose manifest declares this canvas kind. */
function drawnOn(
  rows: readonly AddedActivityRow[],
  canvasesFor: (row: AddedActivityRow) => NamedCanvas[],
  canvasKind: string,
): AddedActivityRow[] {
  return rows.filter((row) => canvasesFor(row).some((one) => one.kind === canvasKind));
}

export function SpaceEditor({
  space,
  address,
  onAddress,
}: {
  readonly space: SpaceRow;
  readonly address: EditorAddress;
  readonly onAddress: (next: EditorAddress) => void;
}) {
  const spaceId = space.space.space_id;
  const activities = useSpaceActivities(spaceId);
  const arrangement = useArrangement(spaceId);
  const arrange = useArrange(spaceId);
  const add = useAddActivity(spaceId);

  const [tool, setTool] = useState<Tool>("interact");
  const [locks, setLocks] = useState<Locks>(() => readLocks(spaceId));
  const [draft, setDraft] = useState<Record<string, Placement> | null>(null);
  /** Whether the tray body is popped out. The column beside it never collapses. */
  const [open, setOpen] = useState(true);
  /** An activity just added and not yet in `rows`, so its slug cannot be addressed until it is. */
  const [placing, setPlacing] = useState<string | null>(null);
  /** The drawn instance selected inside a layer, keyed so one row of a repeat is one selection. */
  const [node, setNode] = useState<StageSelection | null>(null);
  /** The bar's own rendered height, watched so the popped tray body never starts over it: the bar
      wraps to two lines under 1280px and the two rows always stack, so no constant is right. */
  const barRef = useRef<HTMLDivElement>(null);
  const [barHeight, setBarHeight] = useState(0);

  useEffect(() => {
    const element = barRef.current;
    if (element === null) return;
    const measure = (): void => {
      setBarHeight(element.clientHeight);
    };
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(element);
    return () => {
      watch.disconnect();
    };
  }, []);

  // Locks are this tab's session state, kept in sessionStorage rather than lost on navigation
  // away and back. A space change re-reads what this tab remembers for the new space.
  useEffect(() => {
    setLocks(readLocks(spaceId));
  }, [spaceId]);
  useEffect(() => {
    writeLocks(spaceId, locks);
  }, [spaceId, locks]);

  const kinds = declaredKinds(activities);
  const showing =
    address.canvas !== undefined && kinds.includes(address.canvas)
      ? address.canvas
      : (kinds[0] ?? "");

  const slugs = addedActivitySlugs(activities.rows);
  const selected =
    address.select === undefined
      ? undefined
      : addedActivityBySlug(activities.rows, address.select);
  const selectedId = selected?.added_activity_id ?? null;

  /** Whether the selection's pinned manifest is a bundle, which is what Composition edits. */
  const selectedEntry = selected === undefined ? undefined : activities.entryFor(selected);
  const bundled = selectedEntry !== undefined && isBundle(selectedEntry);

  const subject: Subject = useMemo(
    () =>
      selected === undefined
        ? { kind: "space" }
        : { kind: "activity", row: selected, bundle: bundled },
    [selected, bundled],
  );
  /** Cues reads a live scene document and nothing else, so it has nothing to open before one. */
  const hasLiveScene = activities.rows.some((row) => row.mount?.mount_id != null);
  const asked: PanelId | undefined = isPanelId(address.panel) ? address.panel : undefined;
  const offered = useMemo(
    () => offeredPanels(subject, hasLiveScene),
    [subject, hasLiveScene],
  );
  const shown = shownPanel(asked, subject, hasLiveScene);

  const canvasesFor = (row: AddedActivityRow): NamedCanvas[] => {
    const entry = activities.entryFor(row);
    return entry === undefined ? [] : canvasesOf(entry);
  };

  const onThisCanvas = drawnOn(activities.rows, canvasesFor, showing);

  /** The declaration answering to the canvas on screen, which is what decides how it is drawn. */
  const declared = activities.rows
    .flatMap((row) => canvasesFor(row))
    .find((one) => answersTo(one, showing));
  const layout = canvasLayout(declared);
  const arrangeable = placeable(layout);

  const stored = useMemo(
    () => placementsByRow(arrangement.data, showing, activities.rows),
    [arrangement.data, showing, activities.rows],
  );
  const placements = draft ?? stored;

  const order = onThisCanvas.map((row) => row.added_activity_id);
  const ordered = drawOrder(order, placements)
    .map((id) => onThisCanvas.find((row) => row.added_activity_id === id))
    .filter((row): row is AddedActivityRow => row !== undefined);

  const live = ordered.filter((row) => isLive(runStatusOf(row.mount)));

  /** One move of the address, carrying every part this move does not change. */
  const goTo = (next: EditorAddress): void => {
    onAddress({
      canvas: showing === "" ? undefined : showing,
      panel: asked,
      select: address.select,
      ...next,
    });
  };

  /** Selecting an activity pops the tray out on it: the click has to show what it reached. */
  const select = (addedActivityId: string | null): void => {
    if (addedActivityId !== null) setOpen(true);
    goTo({ select: addedActivityId === null ? undefined : slugs.get(addedActivityId) });
  };

  const openPanel = (id: PanelId): void => {
    setOpen(true);
    goTo({ panel: id });
  };

  /** First click takes the layer, a second on the same layer takes the instance under it. */
  const pick = (hit: StageSelection): void => {
    const found = onThisCanvas.find((row) => row.activity_id === hit.activityId);
    const id = found?.added_activity_id ?? null;
    if (id === null) {
      setNode(null);
      select(null);
      return;
    }
    if (id !== selectedId) {
      setNode(null);
      select(id);
      return;
    }
    setNode(hit.nodeKey === undefined ? null : hit);
  };

  /** A click that reached no document at all. */
  const selectNothing = (): void => {
    setNode(null);
    select(null);
  };

  /** An added activity is selected with Layers open, so placing it never needs a pointer. */
  useEffect(() => {
    if (placing === null) return;
    const slug = slugs.get(placing);
    if (slug === undefined) return;
    setPlacing(null);
    setOpen(true);
    onAddress({
      canvas: showing === "" ? undefined : showing,
      panel: "layers",
      select: slug,
    });
  }, [placing, slugs, showing, onAddress]);

  /** V and M pick a tool, a panel's own key opens it, backslash shuts the body, Escape the space. */
  const pressed = (event: KeyboardEvent): void => {
    const target = event.target as HTMLElement | null;
    if (target !== null && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    if (event.key === "Escape") {
      if (node !== null) {
        setNode(null);
        return;
      }
      select(null);
      return;
    }
    if (event.key === "\\") {
      setOpen((was) => !was);
      return;
    }
    const found = TOOLS.find((one) => one.key === event.key.toLowerCase());
    if (found !== undefined) {
      if (found.id === "move" && !arrangeable) return;
      setTool(found.id);
      setDraft(null);
      return;
    }
    if (event.key.length !== 1) return;
    const wanted = panelForKey(event.key, subject, hasLiveScene);
    if (wanted !== undefined) openPanel(wanted);
  };

  const latest = useRef(pressed);
  useEffect(() => {
    latest.current = pressed;
  });
  useEffect(() => {
    const held = (event: KeyboardEvent): void => {
      latest.current(event);
    };
    window.addEventListener("keydown", held);
    return () => {
      window.removeEventListener("keydown", held);
    };
  }, []);

  /** The draft stands until the server answers, so nothing snaps back and then forward again. */
  const commit = (next: Record<string, Placement>): void => {
    setDraft(next);
    arrange.mutate(
      { canvasKind: showing, placements: next },
      {
        onSettled: () => {
          setDraft(null);
        },
      },
    );
  };

  /** Where a drop puts one activity. A flowed canvas has no coordinate, so it only reorders. */
  const place = (addedActivityId: string, point: StagePoint | null): void => {
    if (point !== null && arrangeable) {
      commit({ ...placements, [addedActivityId]: droppedAt(placements[addedActivityId], point) });
      return;
    }
    const order = ordered.map((one) => one.added_activity_id);
    const at = order.indexOf(addedActivityId);
    if (at < 0) return;
    const next = reordered(order, placements, addedActivityId, order.length - 1 - at);
    if (next !== null) commit(next);
  };

  const dropped: StageDrop = (drag, point) => {
    if (drag.from === "added") {
      place(drag.addedActivityId, point);
      return;
    }
    add.mutate(
      { activity_id: drag.activityId, name: drag.name, version: drag.version },
      {
        onSuccess: (added) => {
          setPlacing(added.added_activity_id);
          if (point !== null && arrangeable) {
            commit({ ...placements, [added.added_activity_id]: droppedAt(undefined, point) });
          }
        },
      },
    );
  };

  /** What the stage leaves clear on its right, so the tray body never sits on a drag handle. */
  const trayWidth = trayBodyWidth(open, isWidePanel(shown));

  return (
    <div
      className="cx-editor"
      style={
        {
          "--cx-tray-width": `${String(trayWidth)}px`,
          "--cx-bar-height": `${String(barHeight)}px`,
        } as CSSProperties
      }
    >
      <div className="cx-editor-stage">
        <div className="cx-editor-bar" ref={barRef}>
          <div className="cx-editor-bar-row">
            <div className="cx-seg" role="tablist" aria-label="Canvas">
              {kinds.length === 0 ? <Muted small>no canvas declared yet</Muted> : null}
              {kinds.map((kind) => (
                <button
                  key={kind}
                  type="button"
                  role="tab"
                  aria-selected={kind === showing}
                  className={kind === showing ? "cx-seg-btn cx-seg-btn-on" : "cx-seg-btn"}
                  onClick={() => {
                    goTo({ canvas: kind });
                  }}
                >
                  {kind}
                </button>
              ))}
            </div>
            <div className="cx-tools" role="radiogroup" aria-label="Tool">
              {TOOLS.filter((one) => one.id !== "move" || arrangeable).map((one) => (
                <button
                  key={one.id}
                  type="button"
                  role="radio"
                  aria-checked={tool === one.id}
                  title={`${one.label} (${one.key.toUpperCase()})`}
                  className={tool === one.id ? "cx-tool cx-tool-on" : "cx-tool"}
                  onClick={() => {
                    setTool(one.id);
                    setDraft(null);
                  }}
                >
                  {one.id === "interact" ? <PointerIcon /> : <MoveIcon />}
                  {one.label}
                </button>
              ))}
            </div>
          </div>

          <div className="cx-editor-bar-row cx-editor-bar-operator">
            <div className="cx-presence">
              <Muted small>{`${String(live.length)} of ${String(ordered.length)} here`}</Muted>
              {live.length > 0 ? (
                <StatusBadge tone="success">live</StatusBadge>
              ) : (
                <StatusBadge tone="neutral">stopped</StatusBadge>
              )}
            </div>

            {activities.rows.length > 0 ? (
              <div className="cx-editor-bar-verbs">
                <span className="cx-eyebrow">Acts on the space</span>
                <SpaceVerbs spaceId={spaceId} added={activities.rows} size="sm" />
              </div>
            ) : null}
          </div>
        </div>

        <div
          className="cx-stage"
          onClick={(event) => {
            if (event.target === event.currentTarget) select(null);
          }}
        >
          {tool === "move" && arrangeable ? (
            <span className="cx-stage-hint">Move</span>
          ) : null}
          {activities.rows.length === 0 ? (
            <EmptyState
              headline="Nothing added yet"
              body="Add an activity in the tray and this is where you will see it."
            />
          ) : activities.pending ? (
            <EmptyState bare headline="Reading what is added" />
          ) : live.length === 0 ? (
            <PlanStage
              rows={ordered}
              placements={placements}
              selectedId={selectedId}
              onSelect={select}
              layout={layout}
              onDropActivity={dropped}
              onSelectNothing={selectNothing}
              overlay={
                tool === "move" && arrangeable ? (
                  <ArrangeLayer
                    rows={ordered}
                    placements={placements}
                    locks={locks}
                    selectedId={selectedId}
                    onSelect={select}
                    onDraft={setDraft}
                    onCommit={commit}
                  />
                ) : undefined
              }
            />
          ) : (
            <SpaceCanvas
              rows={ordered}
              canvasKind={showing}
              placements={placements}
              mode={tool === "move" && arrangeable ? "select" : "play"}
              onSelect={pick}
              layout={layout}
              onDropActivity={dropped}
              onSelectNothing={selectNothing}
              overlay={
                <>
                  {arrangeable ? <NodeOutline selection={node} /> : null}
                  {tool === "move" && arrangeable ? (
                    <ArrangeLayer
                      rows={ordered}
                      placements={placements}
                      locks={locks}
                      selectedId={selectedId}
                      onSelect={select}
                      onDraft={setDraft}
                      onCommit={commit}
                    />
                  ) : null}
                </>
              }
            />
          )}
        </div>
      </div>

      <aside
        className={
          !open
            ? "cx-editor-tray cx-editor-tray-shut"
            : isWidePanel(shown)
              ? "cx-editor-tray cx-editor-tray-wide"
              : "cx-editor-tray"
        }
        aria-label="Editor"
      >
        {open ? (
          <div className="cx-tray-body">
            <div className="cx-tray-body-scroll">
              <TrayPanel
                panel={shown}
                subject={subject}
                space={space}
                activities={activities}
                canvasKind={showing}
                drawn={ordered}
                placements={placements}
                locks={locks}
                selectedId={selectedId}
                node={node}
                onSelect={select}
                onLock={(id, locked) => {
                  setLocks((held) => ({ ...held, [id]: locked }));
                }}
                onArrange={commit}
                onOpenPanel={openPanel}
                onAdded={setPlacing}
                placeable={arrangeable}
                arranging={arrange.isPending}
              />
            </div>
            {arrange.isPending || arrange.isError ? (
              <div className="cx-tray-status" aria-live="polite">
                {arrange.isError ? (
                  <StatusBadge tone="danger">{arrange.error.message}</StatusBadge>
                ) : (
                  <Muted small>Arranging</Muted>
                )}
              </div>
            ) : null}
          </div>
        ) : null}
        <PanelColumn
          offered={offered}
          shown={shown}
          open={open}
          onPick={openPanel}
          onToggle={() => {
            setOpen((was) => !was);
          }}
        />
      </aside>
    </div>
  );
}
